import type { EventRow } from './types';
import { NO_DATA, dartRound } from './types';

/**
 * 네 축 비율 + 파생 비율 둘.
 *
 * ⚠️⚠️ **표본이 없으면 `-1`(데이터 없음)이다. 절대 `0` 이 아니다.**
 * 0% 로 돌려주면 미연동 축이 「문제 없음」으로 읽혀 **정반대의 판단**을 부른다.
 * 이 프로젝트에서 실제로 난 사고다 — 검증이 안 도는데 `0.0% 정상` 초록이 떴다.
 */

/** 완주율(%) = `ending_reached` 회차 / `run_started` 회차. */
export function lensCompletionRate(events?: EventRow[] | null): number {
  const started = new Set<string>();
  const ended = new Set<string>();
  for (const e of events ?? []) {
    if (e.eventName === 'run_started') started.add(e.runId);
    if (e.eventName === 'ending_reached') ended.add(e.runId);
  }
  // 0% 로 돌려주면 「아무도 완주 못 함」으로 읽힌다.
  if (started.size === 0) return NO_DATA;
  return (ended.size * 100) / started.size;
}

/**
 * 진행 불가율(%) = 막힌 시도 / 전체 행동 시도.
 *
 * ★ 세 이벤트가 배타적이라는 전제 위에서만 분모가 「행동 턴 수」와 같다.
 */
export function lensBlockedRate(events?: EventRow[] | null): number {
  let attempts = 0;
  let blocked = 0;
  for (const e of events ?? []) {
    if (e.eventName === 'action_interpreted') {
      attempts++;
      if (e.parseOk === false) blocked++;
    }
    if (
      e.eventName === 'input_blocked' ||
      e.eventName === 'unsupported_action_requested'
    ) {
      attempts++;
      blocked++;
    }
  }
  if (attempts === 0) return NO_DATA;
  return (blocked * 100) / attempts;
}

/**
 * AI 오서술률(%) = 검증 실패 / 전체 서술 시도.
 *
 * ★★ **거부 턴도 분모다**(OBXYD 확답 2026-08-07). 규칙 계층이 거부한 턴에도
 * 콜②(서술)가 예외 없이 돌고 과금된다 — 9회차 실측에서 거부 15턴 전부에 콜②
 * 항목이 있었고 누락 0건이었다. 앞의 것만 세면 분모가 조용히 줄어 오서술률이
 * 두 배 넘게 부푼다.
 *
 * ⚠️ `input_blocked` 는 뺀다 — 콜① 이전이라 서술이 돌지 않는다.
 *
 * ⚠️⚠️ **검증이 안 돌면 분모가 거짓말한다.** `narration_validation_failed`
 * 0건이 「돌았고 실패 없음」인지 「아예 안 돎」인지 구분할 수 없다.
 * → `narrationValidated` 가 **한 건이라도 실리면** 분모를 「검증이 실제로 돈
 *   턴」으로 좁힌다. 안 실리면 예전대로 돈다 — **발행되는 순간 저절로 켜진다.**
 */
export function lensNarrationRate(events?: EventRow[] | null): number {
  let turns = 0;
  let failed = 0;
  let validated = 0;
  let sawFlag = false;
  for (const e of events ?? []) {
    if (
      e.eventName === 'action_interpreted' ||
      e.eventName === 'unsupported_action_requested'
    ) {
      turns++;
      // ★ Dart 의 `hasNarrationValidated()` 짝이다. ⚠️ `!= null` 이 아니라
      //   **「실려 있는가」**를 묻는다 — 접힘 때문에 둘이 다르다.
      if (e.narrationValidated !== null) {
        sawFlag = true;
        if (e.narrationValidated === true) validated++;
      }
    }
    if (e.eventName === 'narration_validation_failed') failed++;
  }
  if (sawFlag) turns = validated;
  if (turns === 0) return NO_DATA;
  return (failed * 100) / turns;
}

/**
 * 회차당 원가(원) = Σ `cost_usd` × (1 + 수수료율) × 환율 / 회차 수.
 *
 * ★ `cost_usd` 는 **콜①+콜② 합이고 재시도까지 포함한다**(확답 2026-08-07) —
 *   이 턴에 든 돈 전부다. 따로 더할 것이 없다.
 *
 * ⚠️⚠️ **`cost_usd` 가 없으면 지어내지 않는다.** 예전 폴백(`턴 수 × 단가`)이
 * 「아직 안 보내는 필드」에 걸려 첫 실데이터에서 `18 × 25원 = 450원` 이
 * **초록 「정상」**으로 떴다. 실측 예상 10원 안팎의 45배다.
 *
 * ⚠️ `costPerTurnKrw` 인자는 **안 쓴다.** 커스텀 함수 인자는 덧붙이기만 되고
 *    지울 수 없어서(`_patchFnAddArgs`) 자리만 남았다 — 호출부 정합을 위해 둔다.
 */
export function lensCostPerRun(
  events?: EventRow[] | null,
  _costPerTurnKrw?: number | null,
  usdKrwRate?: number | null,
  costFeeRate?: number | null,
): number {
  const runs = new Set<string>();
  let usd = 0;
  for (const e of events ?? []) {
    if (e.eventName === 'run_started') runs.add(e.runId);
    usd += e.costUsd;
  }
  if (runs.size === 0) return NO_DATA;
  if (usd <= 0) return NO_DATA;
  const krwPerUsd = (usdKrwRate ?? 0) * (1 + (costFeeRate ?? 0) / 100);
  return (usd * krwPerUsd) / runs.size;
}

/**
 * 의도 미해석률 — 콜①이 의도를 판정으로 못 옮긴 턴의 비율.
 *
 * ★★ `reject_unclear` 와 `aside` 를 **합쳐서** 센다. 판단 층이 unclear 를 줄인
 * 것이 아니라 **옮긴 것**이기 때문이다 — 17회차 재집계에서 판단 3→0 ·
 * unclear 0→3 이 됐고 「이름표만 바뀐 것」이었다. 따로 그리면 층이 갈릴 때마다
 * 개선처럼 보인다.
 *
 * ⚠️ `lookup` 은 여기 없다 — 이벤트가 아예 안 나간다.
 */
export function lensUnreadRate(events?: EventRow[] | null): number {
  let attempts = 0;
  let unread = 0;
  for (const e of events ?? []) {
    if (e.eventName === 'action_interpreted') {
      attempts++;
    } else if (
      e.eventName === 'input_blocked' ||
      e.eventName === 'unsupported_action_requested'
    ) {
      attempts++;
      if (e.judgmentKind === 'reject_unclear' || e.judgmentKind === 'aside') {
        unread++;
      }
    }
  }
  if (attempts === 0) return NO_DATA;
  return (unread * 100) / attempts;
}

/** 응답 시간 P95(초). ★ 0 으로 돌려주면 「매우 빠름」으로 읽혀 정반대가 된다. */
export function lensLatencyP95(events?: EventRow[] | null): number {
  const xs: number[] = [];
  for (const e of events ?? []) {
    if (e.durationMs > 0) xs.push(e.durationMs);
  }
  if (xs.length === 0) return NO_DATA;
  xs.sort((a, b) => a - b);
  return xs[dartRound((xs.length - 1) * 0.95)]! / 1000;
}
